import { useState } from "react";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, MagnifyingGlass } from "@phosphor-icons/react";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";
import { PageTitle } from "@/components/layout/DashShell";
import { Loader } from "@/components/Blocks";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const toForm = (f, v) => {
  if (v == null) return f.type === "bool" ? false : "";
  if (f.type === "money") return (v / 100).toFixed(2);
  if (f.type === "json") return JSON.stringify(v, null, 2);
  if (f.type === "list") return (v || []).join("\n");
  if (f.type === "datetime") return toLocalInput(v);
  return v;
};

const fromForm = (f, v) => {
  if (f.type === "money") return Math.round(parseFloat(v || 0) * 100);
  if (f.type === "number") return v === "" ? null : Number(v);
  if (f.type === "json") return JSON.parse(v || "null");
  if (f.type === "list") return String(v).split("\n").map((s) => s.trim()).filter(Boolean);
  if (f.type === "datetime") return v ? new Date(v).toISOString() : "";
  return v;
};

const FieldInput = ({ f, value, onChange, opts }) => {
  const tid = `crud-field-${f.key}`;
  if (f.type === "bool") return <Switch checked={!!value} onCheckedChange={onChange} data-testid={tid} />;
  if (f.type === "textarea" || f.type === "json" || f.type === "list")
    return <Textarea rows={f.type === "json" ? 8 : 4} className={f.type === "json" ? "font-mono-num text-xs" : ""} value={value} onChange={(e) => onChange(e.target.value)} data-testid={tid} />;
  if (f.type === "select") {
    const options = f.options || opts[f.key] || [];
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)} className="h-10 w-full rounded-md border px-3 text-sm" data-testid={tid}>
        <option value="">Choose…</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  return <Input type={f.type === "number" || f.type === "money" ? "number" : f.type === "datetime" ? "datetime-local" : "text"} step={f.type === "money" ? "0.01" : undefined} value={value} onChange={(e) => onChange(e.target.value)} data-testid={tid} />;
};

const Editor = ({ cfg, row, opts, onClose, onSaved }) => {
  const [form, setForm] = useState(() => Object.fromEntries(cfg.fields.map((f) => [f.key, toForm(f, row?.[f.key] ?? f.default)])));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(cfg.fields.map((f) => [f.key, fromForm(f, form[f.key])]));
      if (row?.id) await api.put(`/admin/r/${cfg.resource}/${row.id}`, body);
      else await api.post(`/admin/r/${cfg.resource}`, body);
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(e instanceof SyntaxError ? "Invalid JSON in one of the fields" : errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-white" data-testid="crud-editor-dialog">
        <DialogHeader><DialogTitle>{row?.id ? "Edit" : "New"} {cfg.singular}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {cfg.fields.map((f) => (
            <label key={f.key} className={`block text-sm ${["textarea", "json", "list"].includes(f.type) || f.wide ? "sm:col-span-2" : ""}`}>
              <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-[#475569]">{f.label}</span>
              <FieldInput f={f} opts={opts} value={form[f.key]} onChange={(v) => setForm({ ...form, [f.key]: v })} />
              {f.help && <span className="mt-1 block text-xs text-[#475569]">{f.help}</span>}
            </label>
          ))}
        </div>
        <button className="btn-gold mt-2" disabled={busy} onClick={save} data-testid="crud-save-btn">{busy ? "Saving…" : "Save"}</button>
      </DialogContent>
    </Dialog>
  );
};

export default function CrudPage({ cfg, opts = {} }) {
  const { data, loading, reload } = useFetch(`/admin/r/${cfg.resource}`);
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");
  const del = async (row) => {
    if (!window.confirm(`Delete this ${cfg.singular}?`)) return;
    try { await api.delete(`/admin/r/${cfg.resource}/${row.id}`); toast.success("Deleted"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const rows = (data || []).filter((r) => JSON.stringify(r).toLowerCase().includes(q.toLowerCase()));
  return (
    <>
      <PageTitle title={cfg.title} sub={cfg.sub}>
        {!cfg.readOnly && <button className="btn-gold" onClick={() => setEdit({})} data-testid={`crud-new-${cfg.resource}`}><Plus weight="bold" /> New {cfg.singular}</button>}
      </PageTitle>
      <div className="mb-3 flex max-w-sm items-center gap-2 rounded-md border bg-white px-3"><MagnifyingGlass className="text-[#475569]" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="w-full py-2 text-sm focus:outline-none" data-testid="crud-search-input" /></div>
      {loading ? <Loader /> : (
        <div className="panel overflow-x-auto">
          <table className="w-full text-sm" data-testid={`crud-table-${cfg.resource}`}>
            <thead className="bg-[#F4F6FA] text-left text-xs uppercase tracking-wider text-[#475569]">
              <tr>{cfg.columns.map((c) => <th key={c.key} className="whitespace-nowrap p-3">{c.label}</th>)}<th className="p-3" /></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t hover:bg-[#F4F6FA]/60" data-testid={`crud-row-${r.id}`}>
                  {cfg.columns.map((c) => <td key={c.key} className={`p-3 ${c.mono ? "font-mono-num" : ""}`}>{c.render ? c.render(r, opts) : String(r[c.key] ?? "")}</td>)}
                  <td className="whitespace-nowrap p-3 text-right">
                    <button className="p-1.5 text-[#475569] hover:text-[#0B1B33]" onClick={() => setEdit(r)} aria-label="Edit" data-testid={`crud-edit-${r.id}`}><PencilSimple size={18} /></button>
                    {!cfg.noDelete && <button className="p-1.5 text-[#475569] hover:text-[#C8382E]" onClick={() => del(r)} aria-label="Delete" data-testid={`crud-delete-${r.id}`}><Trash size={18} /></button>}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td className="p-6 text-[#475569]" colSpan={cfg.columns.length + 1}>Nothing here yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {edit && <Editor cfg={cfg} row={edit} opts={opts} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
    </>
  );
}
